import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from "react-router-dom";
import ReactMarkdown from 'react-markdown';
import { getPostBySlug } from '../data/blogPosts';
import { getBlogContent } from '../data/blogContent';
import { getFrameworkContent } from '../data/frameworkContent';

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const content = (post && post.isFramework) ? getFrameworkContent(slug) : getBlogContent(slug);

  useEffect(() => {
    if (post && window.gtag) {
      // Track page view
      window.gtag('config', 'G-8B0H6V9WKC', {
        'page_path': `/blog/${slug}`,
        'page_title': post.title
      });
    }

    // Update meta tags for SEO
    if (post) {
      document.title = `${post.title} | Decision Tracker Blog`;

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', post.excerpt);

      // Update canonical URL for this specific page
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', `https://decisiontrackerbot.com/blog/${slug}`);

      // Update Open Graph tags
      const updateOGTag = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateOGTag('og:title', post.title);
      updateOGTag('og:description', post.excerpt);
      updateOGTag('og:url', `https://decisiontrackerbot.com/blog/${slug}`);
      updateOGTag('og:type', 'article');
      updateOGTag('article:published_time', new Date(post.date).toISOString());
      updateOGTag('article:author', 'Decision Tracker Team');
      updateOGTag('article:section', post.isFramework ? 'Decision Frameworks' : 'Decision Making');
      if (post.featuredImage) {
        updateOGTag('og:image', post.featuredImage);
      }

      // Add keywords meta tag
      if (post.keywords) {
        let keywordsTag = document.querySelector('meta[name="keywords"]');
        if (!keywordsTag) {
          keywordsTag = document.createElement('meta');
          keywordsTag.setAttribute('name', 'keywords');
          document.head.appendChild(keywordsTag);
        }
        keywordsTag.setAttribute('content', post.keywords);
      }

      // Update Twitter Card tags
      const updateTwitterTag = (name, content) => {
        let tag = document.querySelector(`meta[name="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('name', name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      updateTwitterTag('twitter:title', post.title);
      updateTwitterTag('twitter:description', post.excerpt);
      if (post.featuredImage) {
        updateTwitterTag('twitter:image', post.featuredImage);
      }

      // Add Article structured data (JSON-LD)
      const existingScript = document.getElementById('article-structured-data');
      if (existingScript) {
        existingScript.remove();
      }

      const articleSchema = {
        "@context": "https://schema.org",
        "@type": post.isFramework ? "HowTo" : "Article",
        "headline": post.title,
        "description": post.excerpt,
        "image": post.featuredImage || "https://decisiontrackerbot.com/images/logo.png",
        "author": {
          "@type": "Organization",
          "name": "Decision Tracker",
          "url": "https://decisiontrackerbot.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Decision Tracker",
          "logo": {
            "@type": "ImageObject",
            "url": "https://decisiontrackerbot.com/images/logo.png"
          }
        },
        "datePublished": new Date(post.date).toISOString(),
        "dateModified": new Date(post.date).toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://decisiontrackerbot.com/blog/${slug}`
        },
        "keywords": post.keywords,
        "articleSection": post.isFramework ? "Decision Frameworks" : "Decision Making"
      };

      const script = document.createElement('script');
      script.id = 'article-structured-data';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(articleSchema);
      document.head.appendChild(script);

      // Add BreadcrumbList structured data
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://decisiontrackerbot.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://decisiontrackerbot.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": post.title,
            "item": `https://decisiontrackerbot.com/blog/${slug}`
          }
        ]
      };

      const existingBreadcrumb = document.getElementById('breadcrumb-structured-data');
      if (existingBreadcrumb) {
        existingBreadcrumb.remove();
      }

      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.id = 'breadcrumb-structured-data';
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }

    // Cleanup on unmount
    return () => {
      const articleScript = document.getElementById('article-structured-data');
      if (articleScript) articleScript.remove();
      const breadcrumbScript = document.getElementById('breadcrumb-structured-data');
      if (breadcrumbScript) breadcrumbScript.remove();
    };
  }, [slug, post]);

  if (!post) {
    return (
      <div className="dt">
        <section className="dt-hero dt-page-hero">
          <div className="dt-wrap">
            <h1 className="dt-page-title">That post doesn’t exist</h1>
            <p className="dt-hero-sub">It may have moved. <Link to="/blog">See all posts</Link>.</p>
          </div>
        </section>
      </div>
    );
  }

  const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;

  return (
    <div className="dt">
      <article className="dt-article">
        <div className="dt-article-wrap">
          <Link to="/blog" className="dt-back">← All posts</Link>
          <p className="dt-post-meta" style={{ marginTop: 28 }}>
            {new Date(post.date).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <h1 className="dt-page-title dt-article-title">{post.title}</h1>
        </div>
        {post.featuredImage && (
          <div className="dt-article-hero">
            <img src={post.featuredImage} alt={post.imageAlt || ''} />
          </div>
        )}
        <div className="dt-article-wrap dt-prose">
          {content ? (
            <ReactMarkdown
              source={content}
              renderers={{
                link: ({ href, children }) => {
                  const match = href && href.match(youtubeRegex);
                  if (match) {
                    return (
                      <span className="dt-embed">
                        <iframe
                          src={`https://www.youtube.com/embed/${match[1]}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={typeof children === 'string' ? children : 'YouTube video'}
                        />
                      </span>
                    );
                  }
                  const external = href && /^https?:/.test(href);
                  return <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{children}</a>;
                },
                image: ({ src, alt }) => <img src={src} alt={alt || ''} loading="lazy" />,
              }}
            />
          ) : (
            <p>{post.excerpt}</p>
          )}
        </div>
        <div className="dt-article-wrap">
          <div className="dt-article-cta">
            <div>
              <h2 className="dt-h3">Keep the decisions your team makes in Slack</h2>
              <p className="dt-body">Free for 7 days, no card. Then from $3/month per workspace.</p>
            </div>
            <a className="dt-btn dt-btn-primary" href={`${process.env.REACT_APP_HOSTNAME || ''}/trial`}>Add to Slack — free</a>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;



// WEBPACK FOOTER //
// ./src/components/BlogPost.js